import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { go } from 'fuzzysort';
import { debounceTime } from 'rxjs/operators';
import { ConsumptionType } from '../../classes/consumption-type.class';
import { MaterialColor } from '../../classes/material-color.class';
import { MaterialType } from '../../classes/material-type.class';
import { Material } from '../../classes/material.class';
import { RawMaterial } from '../../classes/raw-material.class';
import { Supplier } from '../../classes/supplier.class';
import { CodaltComponent } from '../../codalt.component';
import { SelectItemDialogComponent } from '../../components/select-item-dialog/select-item-dialog.component';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { MaterialService } from '../../services/material/material.service';

type PivotField = 'consumption' | 'width' | 'thickness' | 'coverage_rate';

@Component({
    selector: 'app-materials-management',
    templateUrl: './materials-management.component.html',
    styleUrls: ['./materials-management.component.scss'],
    standalone: false,
})
export class MaterialsManagement extends CodaltComponent implements OnInit, OnDestroy {
    private materialsRaw = signal<Material[]>([]);
    allRawMaterials = signal<RawMaterial[]>([]);
    allColors = signal<MaterialColor[]>([]);
    allSuppliers = signal<Supplier[]>([]);
    consumptionTypes = signal<ConsumptionType[]>([]);
    noRawMaterial = signal(false);
    noMainRawMaterial = signal(false);
    showDeleted = signal(false);

    searchControl = new FormControl('', { nonNullable: true });
    selectedTypeIdsControl = new FormControl<number[]>([], { nonNullable: true });

    searchQuery = toSignal(this.searchControl.valueChanges, { initialValue: this.searchControl.value });
    selectedTypeIds = toSignal(this.selectedTypeIdsControl.valueChanges, {
        initialValue: this.selectedTypeIdsControl.value,
    });

    availableMaterialTypes = signal<MaterialType[]>([]);

    materials = computed(() => {
        const showDeleted = this.showDeleted();
        const noRawMaterial = this.noRawMaterial();
        const noMainRawMaterial = this.noMainRawMaterial();
        const selectedTypeIds = this.selectedTypeIds();
        const searchQuery = this.searchQuery().trim();

        let result = this.materialsRaw()
            .slice()
            .sort((a, b) => {
                const sortName = (n: Material) => {
                    const main = n.raw_materials.find(rm => rm.material_type?.main);
                    return `${main?.name}${n.name}`;
                };
                return sortName(a)?.localeCompare(sortName(b));
            })
            .filter(mat => !!mat.deleted_at === showDeleted)
            .filter(mat =>
                noRawMaterial
                    ? // Waarvan RM zelf verwijderd is
                      (mat.raw_materials?.filter(rm => !!rm.deleted_at)?.length ?? 0) > 0 ||
                      mat.raw_materials?.length === 0
                    : true
            )
            .filter(mat => (noMainRawMaterial ? !this.hasMainRawMaterial(mat) : true))
            .filter(mat =>
                selectedTypeIds.length
                    ? (mat.raw_materials ?? []).some(
                          rm => rm.material_type_id != null && selectedTypeIds.includes(rm.material_type_id)
                      )
                    : true
            );

        if (searchQuery.length) {
            const searchable = result.map(material => ({ material, searchString: this.buildSearchString(material) }));
            const matchedIds = new Set(
                go(searchQuery, searchable, { key: 'searchString', threshold: -10000 }).map(r => r.obj.material.id)
            );
            result = result.filter(mat => matchedIds.has(mat.id));
        }

        return result;
    });

    rawMaterialForms = computed(
        () => new Map(this.allRawMaterials().map(rm => [rm.id, this.buildRawMaterialForm(rm)]))
    );

    private rawMaterialsById = computed(() => new Map(this.allRawMaterials().map(rm => [rm.id, rm])));

    private colorsById = computed(() => new Map(this.allColors().map(c => [c.id, c])));

    private suppliersById = computed(() => new Map(this.allSuppliers().map(s => [s.id, s])));

    constructor(
        private materialService: MaterialService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService
    ) {
        super();
    }

    private hasMainRawMaterial(material: Material): boolean {
        return !!material.raw_materials?.some(rm => rm.material_type?.main && !rm.deleted_at && !rm.pivot?.deleted_at);
    }

    private hasRequiredOfType(material: Material, materialTypeId: number): boolean {
        return !!material.raw_materials?.some(
            rm =>
                rm.material_type_id === materialTypeId &&
                !rm.deleted_at &&
                !rm.pivot?.deleted_at &&
                rm.pivot?.optional !== 1
        );
    }

    addChooseRawMaterial(material: Material) {
        const hasMain = this.hasMainRawMaterial(material);
        const items = this.allRawMaterials()
            .filter(rm => (hasMain ? !rm.material_type?.main : rm.material_type?.main))
            .filter(rm => {
                const materialType = rm.material_type;
                if (!materialType?.single || materialType.main) return true;
                return !this.hasRequiredOfType(material, materialType.id);
            })
            .map(rm => ({
                ...rm,
                displayLabel: `${rm.name}${
                    rm.color ? ` — ${rm.color.name}${rm.color.ral ? ` (${rm.color.ral})` : ''}` : ''
                } · ${rm.supplier?.name ?? '-'} · OD ${rm.od_number ?? '-'} · #${rm.id}`,
            }));

        this.subscriptions.add(
            this.dialog
                .open<SelectItemDialogComponent<RawMaterial>, unknown, RawMaterial>(SelectItemDialogComponent, {
                    width: '400px',
                    data: {
                        title: 'Kies een grondstof',
                        items,
                        bindLabel: 'displayLabel',
                        bindValue: 'id',
                        placeholder: 'Zoek op grondstof, groep, soort, leverancier, kleur...',
                        searchFn: (term, item) => this.matchesRawMaterialSearch(term, item),
                    },
                })
                .afterClosed()
                .subscribe(rawMaterial => {
                    if (rawMaterial) {
                        this.addRM(material, rawMaterial.id);
                    }
                })
        );
    }

    private matchesRawMaterialSearch(term: string, rawMaterial: RawMaterial): boolean {
        const needle = term.trim().toLocaleLowerCase();
        if (!needle.length) return true;

        const haystack = [
            rawMaterial.name,
            rawMaterial.od_number,
            rawMaterial.supplier?.name,
            rawMaterial.material_type?.name,
            rawMaterial.color?.name,
            rawMaterial.color?.ral,
        ]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase();

        return haystack.includes(needle);
    }

    addRM(material: Material, rawMaterialId: number) {
        if (!rawMaterialId) return;

        this.materialService.addRawMaterialToMaterial(material.id, rawMaterialId).subscribe({
            next: () => {
                this.reloadMaterials();
            },
            error: error => {
                console.error('Error adding raw material:', error);
            },
        });
    }
    ngOnInit() {
        this.reload();
        this.materialService.getConsumptionTypes().subscribe(consumptionTypes => {
            this.consumptionTypes.set(consumptionTypes);
        });
    }

    // Volledige herlaad, inclusief de grondstoffen-catalogus (allRawMaterials). Die catalogus
    // verandert nooit door acties op deze pagina (toevoegen/verwijderen koppelt alleen een
    // bestaande grondstof), dus alleen hier nodig, niet na elke kleine actie hieronder — anders
    // herbouwt rawMaterialForms alle FormGroups (en hun subscriptions) voor niets, en verlies je
    // eventuele focus/invoer in "Alle grondstoffen".
    reload() {
        this.materialService.getAllRawMaterials().subscribe(rawMaterials => {
            this.allRawMaterials.set(rawMaterials);
        });
        this.materialService.getColors().subscribe(colors => {
            this.allColors.set(colors);
        });
        this.materialService.getSuppliers().subscribe(suppliers => {
            this.allSuppliers.set([...suppliers].sort((a, b) => a.name.localeCompare(b.name)));
        });
        this.materialService.getMaterialTypes().subscribe(materialTypes => {
            this.availableMaterialTypes.set([...materialTypes].sort((a, b) => a.name.localeCompare(b.name)));
        });
        this.reloadMaterials();
    }

    private buildSearchString(material: Material): string {
        let searchString = material.name ? `${material.name} ` : '';

        for (const rm of material.raw_materials ?? []) {
            if (rm.name) searchString += `${rm.name} `;
            if (rm.od_number) searchString += `${rm.od_number} `;
            if (rm.supplier?.name) searchString += `${rm.supplier.name} `;
            if (rm.material_type?.name) searchString += `${rm.material_type.name} `;
            if (rm.color?.name) searchString += `${rm.color.name} `;
        }

        return searchString;
    }

    reloadMaterials() {
        this.materialService.getList(true, true).subscribe(mat => {
            this.materialsRaw.set(mat);
        });
    }

    // De grondstof komt in twee losse API-responses binnen (per materiaal en in "Alle
    // grondstoffen"), dus twee losse object-instanties voor dezelfde grondstof-id. Lees hier
    // altijd de actuele waarde uit de "Alle grondstoffen"-lijst, anders toont deze tabel na het
    // wisselen van verbruikstype daar nog de oude waarde tot een volledige reload.
    consumptionTypeName(rawMaterial: RawMaterial): string | undefined {
        return (
            this.rawMaterialsById().get(rawMaterial.id)?.consumption_type?.name ?? rawMaterial.consumption_type?.name
        );
    }

    cycleConsumptionType(event: MouseEvent, rawMaterial: RawMaterial) {
        event.preventDefault();
        const types = this.consumptionTypes();
        if (!types.length) return;

        const currentIndex = types.findIndex(type => type.id === rawMaterial.consumption_type_id);
        const nextType = types[(currentIndex + 1) % types.length];

        this.materialService.updateRawMaterial(rawMaterial.id, { consumption_type_id: nextType.id }).subscribe({
            next: () => {
                rawMaterial.consumption_type_id = nextType.id;
                rawMaterial.consumption_type = nextType;
            },
            error: error => {
                console.error('Error updating consumption type:', error);
            },
        });
    }

    editRawMaterial(material: Material, rawMaterial: RawMaterial) {
        this.confirmDialogService
            .confirm({
                title: 'Grondstof verwijderen',
                text: `Weet je zeker dat je grondstof "${rawMaterial.name}" wilt verwijderen van "${material.name}"?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.materialService.deleteRawMaterialFromMaterial(material.id, rawMaterial.id).subscribe({
                        next: () => {
                            this.reloadMaterials();
                        },
                        error: error => {
                            console.error(
                                `Error deleting raw material "${rawMaterial.name}" from "${material.name}":`,
                                error
                            );
                        },
                    });
                },
                () => {}
            );
    }

    private buildRawMaterialForm(rawMaterial: RawMaterial): FormGroup {
        const fg = new FormGroup({
            consumption: new FormControl(rawMaterial.consumption),
            thickness: new FormControl(rawMaterial.thickness),
            width: new FormControl(rawMaterial.width),
            coverage_rate: new FormControl(rawMaterial.coverage_rate),
            color_id: new FormControl(rawMaterial.color_id),
            supplier_id: new FormControl(rawMaterial.supplier_id),
        });

        this.subscriptions.add(
            fg.valueChanges.pipe(debounceTime(300)).subscribe(values => {
                if (!fg.dirty) return;

                this.materialService.updateRawMaterial(rawMaterial.id, values).subscribe({
                    next: () => {
                        const colorChanged = values.color_id !== rawMaterial.color_id;
                        const supplierChanged = values.supplier_id !== rawMaterial.supplier_id;

                        Object.assign(rawMaterial, values);
                        rawMaterial.color = values.color_id ? this.colorsById().get(values.color_id) : undefined;
                        rawMaterial.supplier = values.supplier_id
                            ? this.suppliersById().get(values.supplier_id)
                            : undefined;
                        fg.markAsPristine();

                        if (colorChanged || supplierChanged) {
                            this.reloadMaterials();
                        }
                    },
                    error: error => {
                        console.error('Error updating raw material:', error);
                    },
                });
            })
        );

        return fg;
    }

    // Lege pivotwaarde = standaardwaarde van de grondstof; toon die als placeholder.
    rawMaterialDefault(rawMaterial: RawMaterial, field: PivotField): number | string {
        return (this.rawMaterialsById().get(rawMaterial.id) ?? rawMaterial)[field] ?? '-';
    }

    updatePivotField(pivot: any, field: PivotField, value: string) {
        const parsed = value === '' ? null : Number(value);
        this.materialService.updatePivot(pivot, { [field]: parsed }).subscribe({
            next: () => {
                pivot[field] = parsed;
            },
            error: error => {
                console.error(`Error updating pivot ${field}:`, error);
            },
        });
    }

    toggleOptional(event: MouseEvent, pivot: any) {
        event.preventDefault();
        const newOptionalValue = pivot.optional === 1 ? 0 : 1;
        this.materialService.toggleOptional(pivot, newOptionalValue).subscribe({
            next: () => {
                this.reloadMaterials();
            },
            error: error => {
                console.error('Error toggling optional:', error);
            },
        });
    }
}
